import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, X, Server, Cable, Code2, Shield, Sparkles } from 'lucide-react';
import * as Icons from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';
import { TEAMS_FEATURES, NEW_PAGE_IMAGES } from '../data/mock';

const FAQ = [
  { q: 'What is Microsoft Teams Direct Routing?', a: 'Direct Routing connects Microsoft Teams to the public telephone network using a certified Session Border Controller (SBC). It lets your Teams users place and receive normal phone calls without buying Microsoft Calling Plans — and at a fraction of the cost.' },
  { q: 'Is VoIPinger a certified Direct Routing provider?', a: 'Yes. We use Microsoft-certified SBCs (Ribbon) and our voice routes are tested against Microsoft\'s interoperability standards. We provision tenants in the UK, EU and beyond.' },
  { q: 'What is TeamMate and is the SBC included?', a: 'TeamMate is our Teams integration layer — a TeamMate Connector application plus a Microsoft Direct Routing gateway SBC that comes bundled with the licence. There is no separate SBC to buy or co-locate.' },
  { q: 'How much does it cost compared to Microsoft Calling Plans?', a: 'Most customers save 30–60% versus Microsoft Calling Plans, especially on international calls. We will model your current Calling Plan spend against TeamMate seats and produce a 12-month TCO before you commit.' },
  { q: 'Will my existing numbers work?', a: 'Yes. We port your existing UK or international DDIs onto our network and assign them to Teams users in days, not weeks. Number ranges and non-geographic numbers are both supported.' },
  { q: 'What Microsoft licence do my users need?', a: 'Each user needs a Microsoft Teams Phone licence (or equivalent E5 / A5 / M365 Business Voice). We do not resell Microsoft licences — keep your existing EA / CSP relationship.' },
  { q: 'Can you record Teams calls?', a: 'Yes. Optional compliance-grade call recording is available, with retention rules, encryption and search. PCI-DSS pause/resume on payment lines is supported.' },
  { q: 'How long does setup take?', a: 'Most tenants are live in 48 hours once licences are in place. Number porting is the slowest step at 5–10 working days — but we cut over out-of-hours with a rollback plan.' }
];

export default function MicrosoftTeams() {
  return (
    <>
      <Seo
        title="Microsoft Teams Direct Routing | TeamMate | VoIPinger"
        description="Direct Routing for Microsoft Teams with TeamMate. Certified SBC included, 30–60% cheaper than Calling Plans, UK number porting, optional compliance recording."
        path="/microsoft-teams"
        image="https://voipinger.com/assets/heroes/microsoft-teams.png"
        service={{ name: 'Microsoft Teams Direct Routing — TeamMate', serviceType: 'Microsoft Teams Phone / Direct Routing' }}
        product={{ name: 'TeamMate Direct Routing for Microsoft Teams', description: 'Microsoft-certified Direct Routing SBC, TeamMate Connector, Graph API + Powershell automation, UK and international DDIs.' }}
        faq={FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Microsoft Teams', path: '/microsoft-teams' }]}
      />
      <PageHero
        eyebrow="Microsoft Teams Phone — TeamMate"
        title="Make Microsoft Teams ring with PSTN calls"
        subtitle="Direct Routing for Microsoft Teams — turn Teams into a full-featured business phone system with carrier-grade voice, lower costs and global numbers. Direct Routing gateway SBC included with every TeamMate seat."
        breadcrumb="Microsoft Teams"
      />

      {/* ============================================================== */}
      {/* WHY DIRECT ROUTING — value prop                                 */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">Why Direct Routing</span>
            <h2 className="section-title mt-4">Teams becomes your phone system</h2>
            <p className="section-sub mt-4">
              Keep using Teams for chat, video and meetings — and add proper PSTN calling on top.
              We handle the SBC, the carrier interconnects and the porting; you just enable a licence
              in the Teams admin centre.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2">
              {[
                'Microsoft-certified SBCs',
                'UK & international numbers',
                'Lower cost than Calling Plans',
                'No on-premises hardware',
                'Number porting included',
                'Optional compliance recording',
                'Branded admin portal',
                'PCI pause / resume on recordings'
              ].map((b) => (
                <li key={b} className="flex items-center gap-2 text-slate-700">
                  <Check size={16} className="text-violet-600" />
                  <span className="text-sm">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-primary">Get a Direct Routing quote <ArrowRight size={16} /></Link>
              <a href="tel:+442033551151" className="btn-light"><Phone size={16} /> Talk to engineering</a>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-5 rounded-3xl bg-gradient-to-br from-violet-100 via-purple-50 to-fuchsia-50 -z-10" />
            <video
              src="/assets/videos/voipinger-direct-routing.mp4"
              poster={NEW_PAGE_IMAGES.teams}
              className="rounded-2xl w-full shadow-xl"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="Microsoft Teams Direct Routing"
            />
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* TEAMMATE ARCHITECTURE — the connector + SBC story                */}
      {/* ============================================================== */}
      <section className="relative overflow-hidden bg-gradient-to-b from-violet-50/40 via-white to-white border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Sparkles size={11} /> TeamMate</span>
            <h2 className="section-title mt-4">A Direct Routing stack you don't have to assemble</h2>
            <p className="section-sub mt-3">
              TeamMate is our integration layer between Microsoft Teams and the VoIPinger platform.
              Connector application, certified SBC, provisioning automation — all in one seat
              licence.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-start max-w-5xl mx-auto">
            {/* Architecture diagram (text-rendered) */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-xl p-6 md:p-8">
              <p className="text-xs uppercase tracking-widest font-bold text-violet-600 mb-4">Architecture</p>
              <div className="space-y-3">
                <ArchBox icon="LayoutGrid" label="Microsoft Teams" sub="Your existing tenant" tone="microsoft" />
                <ArchArrow />
                <ArchBox icon="Server" label="Gateway SBC" sub="Microsoft-certified, included" tone="violet" />
                <ArchArrow />
                <ArchBox icon="Cable" label="TeamMate Connector" sub="Call processing + provisioning" tone="fuchsia" />
                <ArchArrow />
                <ArchBox icon="Cloud" label="VoIPinger PSTN" sub="UK + 145 countries" tone="emerald" />
              </div>
              <p className="text-[11px] text-slate-500 mt-5 leading-relaxed">
                Microsoft Graph API + PowerShell automate user assignment — your Teams admin centre
                stays the source of truth.
              </p>
            </div>

            {/* Capabilities list */}
            <div>
              <h3 className="text-2xl font-extrabold text-slate-900 mb-4">What TeamMate adds on top of Teams</h3>
              <div className="space-y-4">
                {[
                  { i: Server, t: 'Certified Direct Routing SBC', d: 'Ribbon SBCs deployed in three geo-redundant clusters. No co-location, no cabinet, no co-management.' },
                  { i: Cable, t: 'TeamMate Connector', d: 'Handles call processing, codec transcoding and tenant provisioning between Microsoft Teams and the VoIPinger core.' },
                  { i: Code2, t: 'Graph API + Powershell', d: 'Users, numbers and routing policies are pushed via Microsoft Graph — no manual Teams Admin Centre clicks per seat.' },
                  { i: Shield, t: 'Compliance & resilience', d: '99.99% uptime SLA, optional PCI-DSS pause/resume on recording, geo-redundant failover between London / Amsterdam / Las Vegas.' }
                ].map((c) => (
                  <div key={c.t} className="flex gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-violet-500/20">
                      <c.i size={18} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">{c.t}</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* CALLING PLANS vs TEAMMATE — comparison                          */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">The numbers</span>
            <h2 className="section-title mt-4">Microsoft Calling Plans vs TeamMate</h2>
            <p className="section-sub mt-3">
              Most UK customers save 30–60% versus Microsoft Calling Plans — and gain the features
              Calling Plans simply doesn't offer.
            </p>
          </div>

          <div className="max-w-5xl mx-auto rounded-2xl border border-slate-200 overflow-hidden shadow-sm bg-white">
            <div className="grid grid-cols-3 text-sm">
              <div className="bg-slate-50 px-4 py-4 font-bold text-slate-700 border-b border-slate-200">Capability</div>
              <div className="bg-slate-50 px-4 py-4 font-bold text-slate-700 border-b border-slate-200 text-center">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Microsoft</div>
                Calling Plans
              </div>
              <div className="bg-gradient-to-br from-violet-600 to-fuchsia-600 px-4 py-4 font-bold text-white border-b border-slate-200 text-center">
                <div className="text-[11px] uppercase tracking-wider text-violet-100/90">VoIPinger</div>
                TeamMate
              </div>

              {[
                { c: 'Per-user monthly cost', m: '£10–£18', v: 'From £8' },
                { c: 'International rates', m: 'Premium', v: 'Wholesale' },
                { c: 'Number porting included', m: 'Limited', v: true },
                { c: 'Non-geographic & international DDIs', m: 'No', v: true },
                { c: 'Compliance call recording', m: 'Add-on', v: true },
                { c: 'PCI-DSS pause / resume', m: false, v: true },
                { c: 'Contact-centre features', m: false, v: true },
                { c: 'Sentiment Analysis + Transcription', m: false, v: true },
                { c: 'Live wallboards', m: false, v: true },
                { c: 'UK support out of London DC', m: false, v: true },
              ].map((row, i) => (
                <React.Fragment key={i}>
                  <div className={`px-4 py-3 border-b border-slate-100 text-slate-900 ${i % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}`}>
                    {row.c}
                  </div>
                  <div className={`px-4 py-3 border-b border-slate-100 text-center ${i % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}`}>
                    {row.m === true ? <Check size={16} className="text-emerald-500 inline" /> : row.m === false ? <X size={16} className="text-rose-400 inline" /> : <span className="text-slate-700">{row.m}</span>}
                  </div>
                  <div className={`px-4 py-3 border-b border-slate-100 text-center bg-violet-50/40 ${i % 2 === 1 ? '' : ''}`}>
                    {row.v === true ? <Check size={16} className="text-violet-600 inline" /> : row.v === false ? <X size={16} className="text-rose-400 inline" /> : <span className="font-bold text-violet-700">{row.v}</span>}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          <p className="text-xs text-slate-500 text-center mt-4">
            Pricing is indicative; we'll model your tenant's actual spend in a free 30-minute review.
          </p>

          <div className="text-center mt-6">
            <Link to="/microsoft-calling-plans-vs-teammate" className="btn-light">
              Open the full comparison + TCO calculator <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* FEATURES GRID — keeps existing TEAMS_FEATURES                    */}
      {/* ============================================================== */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">What you get</span>
            <h2 className="section-title mt-4">Built for IT teams, loved by users</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TEAMS_FEATURES.map((f) => {
              const Ic = Icons[f.icon] || Icons.Sparkles;
              return (
                <div key={f.t} className="card-modern">
                  <div className="w-11 h-11 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center mb-3"><Ic size={20} /></div>
                  <h3 className="font-bold text-slate-900 mb-1">{f.t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* MIGRATION STEPS                                                  */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Migration in 4 steps</span>
            <h2 className="section-title mt-4">Live in days, not months</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {[
              { n: '01', t: 'Discovery', d: 'We map your tenant, current licences, numbers and call flows.' },
              { n: '02', t: 'Provisioning', d: 'TeamMate SBCs configured and routes tested in our lab against your tenant.' },
              { n: '03', t: 'Number porting', d: 'We coordinate with your existing carrier — single DDIs or full ranges.' },
              { n: '04', t: 'Cutover', d: 'Out-of-hours go-live, monitored at our NOC, with a 24-hour rollback plan.' }
            ].map((s) => (
              <div key={s.n} className="card-modern">
                <p className="text-violet-600 font-extrabold text-2xl">{s.n}</p>
                <p className="font-bold text-slate-900 mt-1">{s.t}</p>
                <p className="text-sm text-slate-600 mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqAccordion items={FAQ} title="Microsoft Teams Phone — TeamMate FAQ" />
      <CtaBanner
        eyebrow="Get started"
        title="Ready to enable PSTN calls in Teams?"
        subtitle="Get a Direct Routing quote tailored to your tenant in 24 hours — including TCO vs your current Calling Plan spend."
      />
    </>
  );
}

/* --------------------------- helpers --------------------------- */

const ARCH_TONES = {
  microsoft: { bg: 'bg-sky-50 border-sky-200', icon: 'bg-sky-500 text-white' },
  violet: { bg: 'bg-violet-50 border-violet-200', icon: 'bg-violet-500 text-white' },
  fuchsia: { bg: 'bg-fuchsia-50 border-fuchsia-200', icon: 'bg-fuchsia-500 text-white' },
  emerald: { bg: 'bg-emerald-50 border-emerald-200', icon: 'bg-emerald-500 text-white' },
};

function ArchBox({ icon, label, sub, tone = 'violet' }) {
  const Ic = Icons[icon] || Icons.Server;
  const t = ARCH_TONES[tone] || ARCH_TONES.violet;
  return (
    <div className={`flex items-center gap-3 rounded-xl border ${t.bg} px-4 py-3`}>
      <div className={`w-10 h-10 rounded-lg ${t.icon} flex items-center justify-center shadow-sm`}>
        <Ic size={18} />
      </div>
      <div className="min-w-0">
        <p className="font-bold text-slate-900 text-sm">{label}</p>
        <p className="text-[11px] text-slate-500">{sub}</p>
      </div>
    </div>
  );
}

function ArchArrow() {
  return (
    <div className="flex justify-center text-slate-300">
      <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
        <path d="M9 0v10M5 7l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
